$component-class: 'awards-type-1';

.#{$component-class} {
  background-color: var(--background-color-primary);

  .me-heading {
    color: var(--text-color-primary);
  }

  .me-heading--6 {
    font-size: map-get($font-sizes, lg);
    line-height: map-get($line-heights, md);

    @include below-desktop {
      font-size: map-get($font-sizes, md);
    }
  }

  &__container {
    // NOTE: `align-items` for this element lives in the host-scoped rule at the bottom of
    // this file, not here. Setting it here would tie with me-row's own modifier.

    @include desktop-lg {
      gap: var(--spacing-5xl);

      // 6/6 balanced layout (override base 5/7)
      & > .#{$component-class}__text {
        flex: 6 1 0;
        min-width: 0;
        max-width: none;
      }

      & > .#{$component-class}__logos {
        flex: 6 1 0;
        min-width: 0;
        max-width: none;
      }
    }
  }

  &__text,
  &__logos {
    min-width: 0;
  }

  &__text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-self: center;
    width: 100%;
    min-height: 0;
    box-sizing: border-box;

    .me-heading {
      margin-bottom: 0;

      @include below($breakpoint-desktop-md) {
        max-width: 100%;
      }
    }
  }

  &__logos {
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: center;
    width: 100%;
    min-height: 0;
    box-sizing: border-box;
  }

  &__logos-list {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    gap: var(--spacing-md) var(--spacing-2xl);

    @include desktop {
      gap: var(--spacing-2xl);
    }
  }

  // The flex item is the me-logo HOST element — Stencil gives it the class
  // `hydrated` (not `me-logo`), so it must be targeted by tag. Reserve each badge's
  // design width up-front so the lazy-loaded SVGs keep their box before they load,
  // while still shrinking to fit on narrow rows.
  &__logos-list > me-logo {
    flex: 0 1 112px;
    min-width: 0;
    max-width: 140px;
  }

  // Inner `<a>`/`<span>` the me-logo component renders — fill the reserved host box.
  .me-logo {
    display: block;
    width: 100%;
  }

  // Reserve each badge's box before its (lazy-loaded) SVG loads, so the row holds its
  // size and nothing shifts / pops in on first load.
  // Scoped under `__logos-list` AND matching `:not([width])` on purpose: the global
  // me-logo default `.me-logo img:not([width]) { height: 36px; aspect-ratio: auto 3/1 }`
  // has specificity (0,2,1) — the SAME as a plain `.awards-type-1 .me-logo img` — so it
  // would win by source order and re-introduce the layout shift / shrink the badges to
  // 36px. Adding `__logos-list` bumps this to (0,3,1) so it reliably wins, and
  // `max-height: none` lifts the me-logo height cap.
  &__logos-list .me-logo img:not([width]) {
    display: block;
    width: 100%;
    max-width: 112px;
    max-height: none;
    height: auto;
    aspect-ratio: 112 / 129;
    object-fit: contain;
  }

  // --- layout: split (default) ---
  &--layout-split {
    @include below($breakpoint-desktop-md - 1px) {
      .#{$component-class}__text {
        justify-content: flex-start;
        align-items: center;

        .me-heading {
          text-align: center;
        }
      }

      .#{$component-class}__logos,
      .#{$component-class}__logos-list {
        justify-content: center;
      }
    }

    @include desktop-lg {
      .#{$component-class}__text {
        justify-content: flex-start;
        align-items: flex-start;
      }

      .#{$component-class}__logos,
      .#{$component-class}__logos-list {
        justify-content: flex-end;
      }
    }
  }

  // --- layout: splitReverse ---
  &--layout-splitReverse {
    @include below($breakpoint-desktop-md - 1px) {
      .#{$component-class}__text {
        justify-content: flex-start;
        align-items: center;

        .me-heading {
          text-align: center;
        }
      }

      .#{$component-class}__logos,
      .#{$component-class}__logos-list {
        justify-content: center;
      }
    }

    @include desktop-lg {
      .#{$component-class}__text {
        justify-content: flex-end;
        align-items: flex-end;
      }

      .#{$component-class}__logos,
      .#{$component-class}__logos-list {
        justify-content: flex-start;
      }
    }
  }

  // --- layout: center ---
  &--layout-center {
    @include below($breakpoint-desktop-md - 1px) {
      .#{$component-class}__text {
        justify-content: flex-start;
        align-items: center;

        .me-heading {
          text-align: center;
        }
      }

      .#{$component-class}__logos,
      .#{$component-class}__logos-list {
        justify-content: center;
      }
    }

    @include desktop-lg {
      .#{$component-class}__text {
        justify-content: center;
        align-items: center;

        .me-heading {
          text-align: center;
        }
      }

      .#{$component-class}__logos,
      .#{$component-class}__logos-list {
        justify-content: center;
      }
    }
  }

  // --- layout: spread ---
  &--layout-spread {
    @include below($breakpoint-desktop-md - 1px) {
      .#{$component-class}__text {
        justify-content: flex-start;
        align-items: center;

        .me-heading {
          text-align: center;
        }
      }

      .#{$component-class}__logos,
      .#{$component-class}__logos-list {
        justify-content: center;
      }
    }

    @include desktop-lg {
      .#{$component-class}__text {
        justify-content: flex-start;
        align-items: flex-start;
      }

      .#{$component-class}__logos,
      .#{$component-class}__logos-list {
        justify-content: space-between;
      }
    }
  }

  &--long-text {
    .#{$component-class}__text .me-heading {
      max-width: min(100%, $breakpoint-desktop);
    }
  }
}

// `__container` is the me-row. me-row defaults `alignMd`/`alignLg` to `start`, so it emits
// `me-row--align-md-start` even though this block never asked for it — and that modifier is
// a single class, exactly like `.awards-type-1__container`. Equal specificity (0,1,0) means
// the winner is decided by the document order of the two <style> blocks, which follows the
// critical/deferred split and therefore changes with whatever the page builder puts above
// the fold. Scoping under the host tag makes this (0,1,1) so it wins deterministically.
// Same convention as the me-logo specificity note above and sn-footer-type-1/index.scss.
sn-awards-type-1 .#{$component-class}__container {
  align-items: center;
}
